import { useCallback } from "react";
import { makeVar, ReactiveVar, useReactiveVar } from "@apollo/client";
import { AiChatType } from "constants/field-values";

export interface UploadingFile {
	id: string;
	file: File;
	progress: number;
	status: "uploading" | "converting" | "completed" | "error";
	fileKey?: string;
	gsUri?: string;
	publicUrl?: string;
	mimeType?: string;
	fileName?: string;
	error?: string;
}

// Upload surfaces that are not a chat. They need their own slot so their staged
// files never collide with a chat's attachments (the duplicate check below is
// per-slot, so sharing one would block re-uploading the same file elsewhere).
export enum UploadScope {
	PLAN_REVIEW = "plan_review",
	// The guideline documents of a rule extraction. Its own slot rather than the
	// package one: the two flows stage unrelated files, and a run has to be able to
	// read a document a previous run already read.
	PLAN_REVIEW_GUIDELINES = "plan_review_guidelines",
	// The files on the jurisdiction's Resources screen.
	JURISDICTION_RESOURCES = "jurisdiction_resources"
}

export type UploadingFilesScope = AiChatType | UploadScope;

// One slot per chat type, mirroring the chat status vars.
export const uploadingFilesVar: ReactiveVar<UploadingFile[]> = makeVar<UploadingFile[]>([]);

export const adminUploadingFilesVar: ReactiveVar<UploadingFile[]> = makeVar<UploadingFile[]>([]);

export const ollieUploadingFilesVar: ReactiveVar<UploadingFile[]> = makeVar<UploadingFile[]>([]);

export const planReviewUploadingFilesVar: ReactiveVar<UploadingFile[]> = makeVar<UploadingFile[]>([]);

export const planReviewGuidelinesUploadingFilesVar: ReactiveVar<UploadingFile[]> = makeVar<UploadingFile[]>([]);

export const jurisdictionResourcesUploadingFilesVar: ReactiveVar<UploadingFile[]> = makeVar<UploadingFile[]>([]);

export const getUploadingFilesVar = (scope: UploadingFilesScope = AiChatType.CLIENT): ReactiveVar<UploadingFile[]> => {
	switch (scope) {
		case AiChatType.ADMIN:
			return adminUploadingFilesVar;
		case AiChatType.OLLIE:
			return ollieUploadingFilesVar;
		case UploadScope.PLAN_REVIEW:
			return planReviewUploadingFilesVar;
		case UploadScope.PLAN_REVIEW_GUIDELINES:
			return planReviewGuidelinesUploadingFilesVar;
		case UploadScope.JURISDICTION_RESOURCES:
			return jurisdictionResourcesUploadingFilesVar;
		default:
			return uploadingFilesVar;
	}
};

export const clearUploadingFiles = (scope: UploadingFilesScope = AiChatType.CLIENT) => getUploadingFilesVar(scope)([]);

// Logout cleanup.
export const clearAllUploadingFiles = () => {
	uploadingFilesVar([]);
	adminUploadingFilesVar([]);
	ollieUploadingFilesVar([]);
	planReviewUploadingFilesVar([]);
	planReviewGuidelinesUploadingFilesVar([]);
	jurisdictionResourcesUploadingFilesVar([]);
};

export const useUploadingFiles = (scope: UploadingFilesScope = AiChatType.CLIENT) => {
	const filesVar = getUploadingFilesVar(scope);
	const uploadingFiles = useReactiveVar(filesVar);

	const updateUploadingFiles = useCallback(
		(updater: UploadingFile[] | ((prev: UploadingFile[]) => UploadingFile[])) => {
			if (typeof updater === "function") {
				filesVar(updater(filesVar()));
			} else {
				filesVar(updater);
			}
		},
		[filesVar]
	);

	const clearFiles = useCallback(() => filesVar([]), [filesVar]);

	return { uploadingFiles, updateUploadingFiles, clearUploadingFiles: clearFiles };
};
